import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startListening, stopListening } from "../../redux/animationSlice";
import { logoutUserAsync } from "../../redux/usersSlice";
import { FaMicrophone, FaStop, FaPaperPlane } from "react-icons/fa";
import Avatar from "@mui/material/Avatar";
import "./styles/VoiceChatUI.css";
import { speechToText, chatWithAssistant, getUserProfilePicture } from "../../services/apiService";
import ChatIconImage2 from "../../assets/icons/profile2.png";  
import { getMedusaInspiredColor, getInitials } from "../../utils/utils";
// Helper functions for date/time
const getCurrentHour = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const getCurrentDay = () => new Date().toLocaleDateString();

const MAX_CHARACTERS = 600;





export default function VoiceChatUI({
  conversationHistory,
  setConversationHistory,
  userMessage,
  setUserMessage,
  sendMessage,
}) {
  const dispatch = useDispatch();
  const chatMessagesRef = useRef(null);

  // Current user / profiles from Redux
  const currentUser = useSelector((state) => state.users.currentUser);
  const currentUserProfiles = useSelector(
    (state) => state.profiles.currentUserProfiles
  );
  const isUser =
    currentUser?.roles && currentUser.roles.includes("ROLE_USER");
  const hasUserCandidatureProfile =
    isUser && currentUserProfiles && currentUserProfiles.length > 0;

  // Suggestions based on role
  let suggestions = [];
  if (isUser) {
    suggestions = ["Je veux consulter mon profil", "Je veux me déconnecter"];
    if (!hasUserCandidatureProfile) {
      suggestions.push("Je veux ajouter mon profil de candidature");
    }
    suggestions.push("Je veux consulter mon profil de candidature");
  } else {
    suggestions = [
      "Je cherche des profils avec la recherche standard",
      "Je veux consulter mon profil",
      "Je veux me déconnecter",
      "Je cherche un data scientist à paris ayant les compétences python et nltk débutant",
      "Je veux ajouter des profils de candidature",
      "Je veux consulter mes profils de candidature",
      "Je veux consulter mon historique de conversation",
      "Je veux consulter mes recherches sauvegardées",
    ];
  }

  // UI state

  const [isBotRespondingLocal, setIsBotRespondingLocal] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessingSpeech, setIsProcessingSpeech] = useState(false);

  // Refs for audio recording
  const mediaRecorderRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop =
        chatMessagesRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  // Render bubble (text or link)
  const renderMessageBubble = (message) => {
    // If it's a link, render as before
    if (message.linkUrl) {
      return (
        <a
          href={message.linkUrl}
          onClick={(e) => {
            e.preventDefault();
            window.open(message.linkUrl, "_blank");
          }}
        >
          {message.text}
        </a>
      );
    }

    // If message.text is an object with question/options, render clearly
    if (
      typeof message.text === "object" &&
      message.text !== null &&
      (message.text.question || message.text.options)
    ) {
      return (
        <div>
          {message.text.question && (
            <div style={{ marginBottom: "8px" }}>{message.text.question}</div>
          )}
          {Array.isArray(message.text.options) && (
            <ul className="bot-options-list" style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
              {message.text.options.map((opt, idx) => (
                <li key={idx} style={{ marginBottom: "4px", padding: 0 }}>
                  {opt}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    // Fallback: render as plain text
    const messageText =
      typeof message.text === "object"
        ? JSON.stringify(message.text)
        : message.text;
    return <span>{messageText}</span>;
  };

  // Send typed message
  const handleSend = async () => {
    if (!userMessage.trim()) return;
    const userMsg = {
      sender: "user",
      text: userMessage,
      createdAtDay: getCurrentDay(),
      createdAtHour: getCurrentHour(),
    };
    setConversationHistory((prev) => [...prev, userMsg]);
    setIsBotRespondingLocal(true);
    setUserMessage(""); // Clear input immediately after sending

    try {
      const response = await chatWithAssistant(
        { user_input: userMessage },
        dispatch,
        logoutUserAsync
      );

      if (response?.type === "chat") {
        const botMsg = {
          sender: "bot",
          text: response.response,
          createdAtDay: getCurrentDay(),
          createdAtHour: getCurrentHour(),
        };
        setConversationHistory((prev) => [...prev, botMsg]);
      } else if (response?.type === "profiles") {
        const newMessages = [];

        // String response
        if (
          response.response &&
          typeof response.response === "string" &&
          response.response.trim()
        ) {
          newMessages.push({
            sender: "bot",
            text: response.response,
            createdAtDay: getCurrentDay(),
            createdAtHour: getCurrentHour(),
          });
        }
        // Object response: show question/options as object for custom rendering
        else if (
          response.response &&
          typeof response.response === "object"
        ) {
          // Instead of combining to string, push the object directly
          newMessages.push({
            sender: "bot",
            text: response.response,
            createdAtDay: getCurrentDay(),
            createdAtHour: getCurrentHour(),
          });
        }

        // Always append clickable link
        const encodedDesc = encodeURIComponent(
          response.description || userMessage
        );
        newMessages.push({
          sender: "bot",
          text: "Cliquez ici pour consulter les profils trouvés",
          linkUrl: `/chat-results?desc=${encodedDesc}`,
          createdAtDay: getCurrentDay(),
          createdAtHour: getCurrentHour(),
        });

        setConversationHistory((prev) => [...prev, ...newMessages]);
      }

      if (sendMessage) await sendMessage(userMessage);
    } catch (error) {
      console.error("Erreur lors de l'appel à /chat :", error);
      setConversationHistory((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Oups, une erreur s'est produite...",
          createdAtDay: getCurrentDay(),
          createdAtHour: getCurrentHour(),
        },
      ]);
    } finally {
      setIsBotRespondingLocal(false);
      // setUserMessage(""); // <-- Remove this line, already cleared above
    }
  };

  // Click on microphone button
  const handleMicClick = async () => {
    if (isProcessingSpeech) return;
    if (isListening) {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        clearTimeout(stopTimeoutRef.current);
        mediaRecorderRef.current.stop();
        setIsListening(false);
      }
      return;
    }
    try {
      setIsListening(true);
      dispatch(startListening());
      const audioFile = await getAudioFile();
      setIsListening(false);
      dispatch(stopListening());

      setIsProcessingSpeech(true);
      const response = await speechToText(audioFile);
      if (response && response.text) {
        setUserMessage(response.text);
      }
    } catch (error) {
      console.error("Erreur de conversion de la parole en texte :", error);
    } finally {
      setIsProcessingSpeech(false);
    }
  };

  // Record audio
  const getAudioFile = () => {
    return new Promise(async (resolve, reject) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        const audioChunks = [];

        mediaRecorder.ondataavailable = (event) =>
          audioChunks.push(event.data);

        mediaRecorder.onstop = async () => {
          const originalBlob = new Blob(audioChunks, {
            type: mediaRecorder.mimeType,
          });
          try {
            const wavBlob = await convertBlobToWav(originalBlob);
            const audioFile = new File([wavBlob], "audio.wav", {
              type: "audio/wav",
            });
            resolve(audioFile);
          } catch (err) {
            reject(err);
          }
        };

        mediaRecorder.onerror = (event) => reject(event.error);
        mediaRecorder.start();
        // Auto‐stop after 8s if needed
        stopTimeoutRef.current = setTimeout(() => {
          if (mediaRecorder.state === "recording") {
            mediaRecorder.stop();
            setIsListening(false);
          }
        }, 8000);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Convert Blob → WAV
  const convertBlobToWav = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result;
        const audioCtx = new AudioContext();
        audioCtx.decodeAudioData(
          arrayBuffer,
          (audioBuffer) => {
            try {
              const wavBuffer = encodeWAV(audioBuffer);
              const wavBlob = new Blob([wavBuffer], {
                type: "audio/wav",
              });
              resolve(wavBlob);
            } catch (e) {
              reject(e);
            }
          },
          (err) => reject(err)
        );
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(blob);
    });
  };

  // Encode PCM → WAV DataView
  const encodeWAV = (audioBuffer) => {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitsPerSample = 16;

    let interleaved;
    if (numChannels === 2) {
      interleaved = interleave(
        audioBuffer.getChannelData(0),
        audioBuffer.getChannelData(1)
      );
    } else {
      interleaved = audioBuffer.getChannelData(0);
    }

    const buffer = new ArrayBuffer(44 + interleaved.length * 2);
    const view = new DataView(buffer);

    // RIFF chunk descriptor
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + interleaved.length * 2, true);
    writeString(view, 8, "WAVE");

    // fmt sub-chunk
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true); // PCM header size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(
      28,
      sampleRate * numChannels * (bitsPerSample / 8),
      true
    );
    view.setUint16(32, numChannels * (bitsPerSample / 8), true);
    view.setUint16(34, bitsPerSample, true);

    // data sub-chunk
    writeString(view, 36, "data");
    view.setUint32(40, interleaved.length * 2, true);

    // PCM samples
    let offset = 44;
    for (let i = 0; i < interleaved.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, interleaved[i]));
      view.setInt16(
        offset,
        s < 0 ? s * 0x8000 : s * 0x7fff,
        true
      );
    }

    return view;
  };

  const writeString = (view, offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  const interleave = (inputL, inputR) => {
    const length = inputL.length + inputR.length;
    const result = new Float32Array(length);
    let index = 0,
      i = 0;
    while (index < length) {
      result[index++] = inputL[i];
      result[index++] = inputR[i];
      i++;
    }
    return result;
  };

  // Add local state for profilePictureUrl (like in Profile.jsx)
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);

  useEffect(() => {
    // Get profile picture URL if needed
    if (currentUser?.profilePicture) {
      getUserProfilePicture(currentUser.profilePicture)
        .then((url) => setProfilePictureUrl(url))
        .catch(() => setProfilePictureUrl(null));
    } else {
      setProfilePictureUrl(null);
    }
  }, [currentUser]);

  // Render
  return (
    <div className="chat-container">
      {/* Header */}
      <header className="chat-header">
   {/*      {isListening ? (
          <div className="nebula-spinner">
            <div className="circle" />
            <div className="circle" />
            <div className="circle" />
          </div>
        ) : isBotRespondingLocal ? (
          <div className="loading-dots">
            <span />
            <span />
            <span />
          </div>
        ) : (
          <img src={ChatIconImage} alt="Bot" className="bot-logo" />
        )} */}

      </header>

   

      {/* Messages */}
      <div className="chat-messages" ref={chatMessagesRef}>
        {/* Message de bienvenue */}
        <div className="message-row bot-row">
          <div className="chat-bubble bot-bubble">
            Bienvenue sur Searchy ! Un chat intelligent qui gère profil,
            redirections, déconnexion et trouve des profils adaptés à
            vos besoins.
          </div>
          <img
            src={ChatIconImage2}
            alt="Bot"
            className="avatar bot-avatar"
          />
        </div>

        {/* Historique */}
        {conversationHistory?.map((m, i) => (
          <div
            key={i}
            className={`message-row ${
              m.sender === "user" ? "user-row" : "bot-row"
            }`}
          >
            {m.sender === "user" && (
              <div className="avatar-container">
                <Avatar
                  src={profilePictureUrl}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: profilePictureUrl
                      ? "transparent"
                      : getMedusaInspiredColor(currentUser?.username),
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                >
                  {!profilePictureUrl && getInitials(currentUser?.username)}
                </Avatar>
              </div>
            )}
            <div
              className={`chat-bubble ${
                m.sender === "user" ? "user-bubble" : "bot-bubble"
              }`}
            >
              {renderMessageBubble(m)}
            </div>
            {m.sender === "bot" && (
              <img
                src={ChatIconImage2}
                alt="Bot"
                className="avatar bot-avatar"
              />
            )}
          </div>
        ))}

        {/* Indicateur enregistrement */}
        {(isListening || isProcessingSpeech) && (
          <div className="message-row user-row">
            <div className="avatar-container">
              <Avatar
                src={profilePictureUrl}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: profilePictureUrl
                    ? "transparent"
                    : getMedusaInspiredColor(currentUser?.username),
                  color: "#fff",
                  fontWeight: "bold",
                }}
              >
                {!profilePictureUrl && getInitials(currentUser?.username)}
              </Avatar>
            </div>
            <div className="chat-bubble user-bubble">
              <div className="nebula-spinner">
                <div className="circle" />
                <div className="circle" />
                <div className="circle" />
              </div>
            </div>
          </div>
        )}

        {/* Indicateur réponse bot */}
        {isBotRespondingLocal && (
          <div className="message-row bot-row">
            <div className="chat-bubble bot-bubble">
              <div className="loading-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
            <img
              src={ChatIconImage2}
              alt="Bot"
              className="avatar bot-avatar"
            />
          </div>
        )}
      </div>

      {/* Suggestions */}
      <div className="suggestions-container">
        {suggestions.map((s, idx) => (
          <button
            key={idx}
            className="suggestion-bubble"
            onClick={() => setUserMessage(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Input row */}
      <div className="chat-input-container">
        <textarea
          className="chat-input"
          placeholder="Écrivez un message…"
          value={userMessage}
          onChange={(e) =>
            e.target.value.length <= MAX_CHARACTERS &&
            setUserMessage(e.target.value)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (userMessage.trim()) handleSend();
            }
          }}
        />
        <span className="character-count">
          {userMessage.length}/{MAX_CHARACTERS}
        </span>
        <button
          className="send-btn"
          onClick={handleSend}
          disabled={!userMessage.trim()}
        >
          <FaPaperPlane />
        </button>
        <button
          className={`mic-btn ${isListening ? "listening" : ""}`}
          onClick={handleMicClick}
        >
          {isListening ? <FaStop /> : <FaMicrophone />}
        </button>
      </div>
    </div>
  );
}
